import React, { useEffect } from 'react'
import { Button, Card, Flex, Form, Input, notification, Skeleton, Typography } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { TimeAgo } from '../../components/time-ago'
import { useAuth } from '../../auth'
import { UPDATE_SITE } from '../../user/permission'
import { CrawlInput } from '../api'
import { useCrawlSettings, useUpdateCrawlSettings } from '../hooks'

type SitemapValues = Pick<CrawlInput, 'sitemapUrl'>

/**
 * How Evaa reads a website that uses the snippet: from its sitemap and links. Vacancies and pages to skip are marked in
 * the pages themselves.
 */
export function SiteCrawl({ siteId }: { siteId: string }) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const { data } = useCrawlSettings(siteId)
    const update = useUpdateCrawlSettings(siteId)
    const [form] = Form.useForm<SitemapValues>()

    useEffect(() => {
        if (data) {
            form.setFieldsValue({ sitemapUrl: data.sitemapUrl })
        }
    }, [data, form])

    if (!data) {
        return <Skeleton active />
    }

    const save = (values: SitemapValues) => {
        const sitemapUrl = values.sitemapUrl?.trim() || null
        const message = sitemapUrl === data.sitemapUrl ? t('Settings saved') : t('Saved. Evaa reads the website again.')

        // The vacancy and skip rules are set in the pages above, so they are kept as they are.
        update.mutate(
            {
                sitemapUrl,
                exclude: data.exclude,
                vacancyPaths: data.vacancyPaths,
                notVacancyPaths: data.notVacancyPaths,
            },
            {
                onSuccess: () => notification.success({ message }),
            }
        )
    }

    return (
        <Card title={t('How Evaa reads your website')}>
            <Flex
                vertical
                gap="middle"
            >
                <Typography.Text type="secondary">
                    {t(
                        'Evaa reads your website once a day, up to {{count}} pages. It starts with the list of pages of your website and follows the links on your own website. Evaa does not follow links to other websites, or pages your website hides from search engines.',
                        { count: data.maxPages }
                    )}
                </Typography.Text>
                <Typography.Text type="secondary">
                    {t(
                        "Evaa recognises vacancies by the vacancy details that Google uses too. Most applicant tracking systems and website builders add them by themselves. Doesn't Evaa recognise a vacancy? Mark it as a vacancy in the pages above. Pages Evaa does not need to read, such as news articles, you skip there too."
                    )}
                </Typography.Text>
                <Typography.Text>
                    <CrawlStatus
                        crawling={data.crawling}
                        lastCrawledAt={data.lastCrawledAt}
                    />
                </Typography.Text>
                <Form
                    form={form}
                    layout="vertical"
                    disabled={!can(UPDATE_SITE)}
                    onFinish={save}
                >
                    <Form.Item
                        name="sitemapUrl"
                        label={t('List of pages (sitemap)')}
                        extra={t(
                            'Usually not needed: Evaa finds this list itself. Only fill in a link if your website builder says the list is somewhere else.'
                        )}
                        rules={[
                            {
                                type: 'url',
                                message: t('Enter a full link, starting with https://'),
                            },
                        ]}
                    >
                        <Input placeholder="https://www.example.com/sitemap.xml" />
                    </Form.Item>
                    {can(UPDATE_SITE) && (
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={update.isPending}
                        >
                            {t('Save')}
                        </Button>
                    )}
                </Form>
            </Flex>
        </Card>
    )
}

type CrawlStatusProps = {
    crawling: boolean
    lastCrawledAt: Date | null
}

function CrawlStatus({ crawling, lastCrawledAt }: CrawlStatusProps) {
    const { t } = useTranslation()

    if (crawling) {
        return (
            <>
                <LoadingOutlined /> {t('Evaa is reading the website now.')}
            </>
        )
    }

    if (!lastCrawledAt) {
        return t('Not read yet')
    }

    return (
        <>
            {t('Last read')} <TimeAgo date={lastCrawledAt} />
        </>
    )
}
